import React, { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import ProgressTracker from '../../../components/ui/ProgressTracker';

const VendorImportModal = ({ isOpen, onClose, onImport }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping, 3: Preview, 4: Processing
  const { currentOrgId } = useAuth();
  const [file, setFile] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [csvTotalRows, setCsvTotalRows] = useState(0);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateHandling, setDuplicateHandling] = useState('skip');

  const csvFields = [
    'Company Name',
    'Trading Name',
    'Contact Name',
    'Email',
    'Phone',
    'Account Reference',
    'VAT Number',
    'Address Line 1',
    'Address Line 2',
    'City',
    'County',
    'Postcode',
    'Country'
  ];

  const requiredFields = ['Company Name', 'Account Reference'];

  const duplicateOptions = [
    { value: 'skip', label: 'Skip duplicates' },
    { value: 'update', label: 'Update existing records' },
    { value: 'create', label: 'Create new records with suffix' }
  ];

  const handleFileUpload = (event) => {
    const uploadedFile = event?.target?.files?.[0];
    if (!uploadedFile) return;
    if (!/\.csv$/i.test(uploadedFile?.name || '')) return;
    setFile(uploadedFile);
    // Parse first ~100 rows for preview and capture headers
    Papa.parse(uploadedFile, {
      header: true,
      preview: 100,
      skipEmptyLines: true,
      worker: true,
      complete: (res) => {
        const rows = Array.isArray(res?.data) ? res.data.filter(Boolean) : [];
        const headers = res?.meta?.fields || Object.keys(rows?.[0] || {});
        setCsvHeaders(headers);
        // Default mapping: identity where possible
        const defaultMap = {};
        headers.forEach(h => { defaultMap[h] = h; });
        setFieldMapping(defaultMap);
        setPreviewData(rows.slice(0, 20));
        setStep(2);
        // In the background, count all rows for accurate metrics
        Papa.parse(uploadedFile, {
          header: true,
          skipEmptyLines: true,
          worker: true,
          complete: (full) => {
            const total = Array.isArray(full?.data) ? full.data.filter(Boolean).length : 0;
            setCsvTotalRows(total);
          }
        });
      },
      error: () => {
        setCsvHeaders([]);
        setPreviewData([]);
        setFieldMapping({});
        setCsvTotalRows(0);
      }
    });
  };

  const handleFieldMapping = (csvColumn, systemField) => {
    setFieldMapping(prev => ({
      ...prev,
      [csvColumn]: systemField
    }));
  };

  const handlePreview = () => {
    setStep(3);
  };

  const handleImport = () => {
    if (!file) return;
    if (!currentOrgId) {
      alert('No organization selected. Please select or create an organization first.');
      return;
    }
    setStep(4);
    setImportProgress(0);

    const BATCH = 1000;
    let total = 0, processed = 0, skipped = 0;
    const rowsBuffer = [];
    const seenRefs = new Set();

    const mapRowToVendor = (row) => {
      // Resolve the source CSV column for a given system label via reverse mapping
      const get = (label) => {
        const entry = Object.entries(fieldMapping || {}).find(([, dest]) => dest === label);
        const sourceCol = entry ? entry[0] : label;
        return row?.[sourceCol] ?? row?.[label] ?? '';
      };
      const company = String(get('Company Name') || '').trim();
      const trading = String(get('Trading Name') || '').trim();
      const accountRef = String(get('Account Reference') || '').trim();
      const vat = String(get('VAT Number') || '').trim();
      if (!company || !accountRef) return null;
      // Map into our schema: prefer trading as display, keep company as legal
      const displayName = trading || company;
      const legalName = company;
      return {
        org_id: currentOrgId || null,
        account_ref: accountRef,
        display_name: displayName,
        legal_name: legalName,
        vat_reg_no: vat || null,
        active: true,
        source_file: file?.name || null
      };
    };

    const flush = async (payload) => {
      if (!payload?.length) return;
      try {
        if (duplicateHandling === 'skip') {
          await supabase.from('vendors').upsert(
            payload,
            { onConflict: 'account_ref', ignoreDuplicates: true }
          );
        } else if (duplicateHandling === 'update') {
          await supabase.from('vendors').upsert(
            payload,
            { onConflict: 'account_ref' }
          );
        } else {
          // create-with-suffix: try insert; on conflict, append -IMP<n>
          for (const v of payload) {
            let ref = v.account_ref;
            let attempt = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const { error } = await supabase.from('vendors').insert([{ ...v, account_ref: ref }], { onConflict: 'account_ref', ignoreDuplicates: true });
              if (!error) break;
              attempt++;
              ref = `${v.account_ref}-IMP${attempt}`;
              if (attempt > 5) break;
            }
          }
        }
      } catch (e) {
        // best-effort: count as skipped
        skipped += payload.length;
      }
    };

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      worker: true,
      step: async (results, parser) => {
        total++;
        const r = results?.data || {};
        const v = mapRowToVendor(r);
        if (!v || seenRefs.has(v.account_ref)) { skipped++; return; }
        seenRefs.add(v.account_ref);
        rowsBuffer.push(v);
        processed++;
        if (rowsBuffer.length >= BATCH) {
          parser.pause();
          await flush(rowsBuffer.splice(0, rowsBuffer.length));
          setImportProgress(Math.round((processed / Math.max(1, (csvTotalRows || total))) * 100));
          parser.resume();
        }
      },
      complete: async () => {
        await flush(rowsBuffer);
        setImportProgress(100);
        onImport?.({ file, mapping: fieldMapping, duplicateHandling, recordsProcessed: processed, skipped, total });
        onClose();
        resetModal();
      }
    });
  };

  const resetModal = () => {
    setStep(1);
    setFile(null);
    setFieldMapping({});
    setCsvHeaders([]);
    setPreviewData([]);
    setImportProgress(0);
    setDuplicateHandling('skip');
  };

  const handleClose = () => {
    onClose();
    resetModal();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose} />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-moderate w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Import Vendors</h2>
              <p className="text-sm text-muted-foreground">
                Import vendor data from Sage 50 CSV export
              </p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-4">
            {[
              { step: 1, label: 'Upload File', icon: 'Upload' },
              { step: 2, label: 'Map Fields', icon: 'ArrowRightLeft' },
              { step: 3, label: 'Preview', icon: 'Eye' },
              { step: 4, label: 'Import', icon: 'CheckCircle' }
            ]?.map((item, index) => (
              <div key={item?.step} className="flex items-center">
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-full border-2 transition-smooth
                  ${step >= item?.step 
                    ? 'bg-primary border-primary text-primary-foreground' 
                    : 'border-border text-muted-foreground'
                  }
                `}>
                  {step > item?.step ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={item?.icon} size={16} />
                  )}
                </div>
                <span className={`
                  ml-2 text-sm font-medium
                  ${step >= item?.step ? 'text-foreground' : 'text-muted-foreground'}
                `}>
                  {item?.label}
                </span>
                {index < 3 && (
                  <Icon name="ChevronRight" size={16} className="mx-3 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Upload CSV File</h3>
                <p className="text-muted-foreground mb-6">
                  Select your Sage 50 vendor export CSV file to begin the import process.
                </p>
              </div>

              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center space-y-4"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Upload" size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">CSV files only</p>
                  </div>
                </label>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">CSV Format Requirements:</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• First row must contain column headers</li>
                  <li>• Required fields: Company Name, Account Reference</li>
                  <li>• UTF-8 encoding recommended</li>
                  <li>• Maximum file size: 10MB</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Field Mapping */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Map CSV Fields</h3>
                <p className="text-muted-foreground">
                  Map your CSV columns to the corresponding system fields.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(csvHeaders || [])?.map((csvColumn) => (
                  <div key={csvColumn} className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {csvColumn}
                      {requiredFields?.includes(csvColumn) && (
                        <span className="text-error ml-1">*</span>
                      )}
                    </label>
                    <Select
                      options={[
                        { value: '', label: 'Do not import' },
                        ...csvFields?.map(field => ({ value: field, label: field }))
                      ]}
                      value={fieldMapping?.[csvColumn] || csvColumn}
                      onChange={(value) => handleFieldMapping(csvColumn, value)}
                      placeholder="Select field..."
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-foreground">Duplicate Handling</h4>
                <Select
                  label="How should duplicate records be handled?"
                  options={duplicateOptions}
                  value={duplicateHandling}
                  onChange={setDuplicateHandling}
                />
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Preview Import</h3>
                <p className="text-muted-foreground">
                  Review the data that will be imported. {(csvTotalRows || previewData?.length || 0)} records found.
                </p>
              </div>

              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        {csvFields?.filter(field => 
                          Object.values(fieldMapping)?.includes(field) || 
                          (csvHeaders || []).includes(field)
                        )?.map((field) => (
                          <th key={field} className="text-left px-4 py-3 font-medium text-foreground">
                            {field}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData?.slice(0, 5)?.map((row, index) => (
                        <tr key={index} className="border-b border-border">
                          {csvFields
                            ?.filter(field =>
                              Object.values(fieldMapping)?.includes(field) ||
                              (csvHeaders || []).includes(field)
                            )
                            ?.map((field) => {
                              // Find the CSV column mapped to this system field label
                              const entry = Object.entries(fieldMapping || {}).find(([, dest]) => dest === field);
                              const sourceCol = entry ? entry[0] : field;
                              const val = row?.[sourceCol];
                              return (
                                <td key={field} className="px-4 py-3 text-muted-foreground">
                                  {val != null && val !== '' ? String(val) : '-'}
                                </td>
                              );
                            })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {(csvTotalRows || previewData?.length || 0) > 5 && (
                <p className="text-xs text-muted-foreground text-center">
                  Showing first 5 records. {(Math.max(0, (csvTotalRows || previewData?.length || 0) - 5))} more records will be imported.
                </p>
              )}
            </div>
          )}

          {/* Step 4: Processing */}
          {step === 4 && (
            <div className="space-y-6 text-center">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Importing Vendors</h3>
                <p className="text-muted-foreground">
                  Please wait while we process your vendor data...
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <ProgressTracker
                  progress={importProgress}
                  status="processing"
                  label="Processing vendor records"
                  variant="default"
                  estimatedTime={Math.max(1, Math.round((100 - importProgress) / 10 * 0.2))}
                  onCancel={() => {
                    setStep(1);
                    resetModal();
                  }}
                />
              </div>

              <div className="text-sm text-muted-foreground">
                {Math.round((importProgress / 100) * (csvTotalRows || 0))} of {(csvTotalRows || 0)} records processed
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {step === 2 && `${(csvHeaders || []).length} columns detected`}
            {step === 3 && `${(csvTotalRows || previewData?.length || 0)} records ready to import`}
          </div>
          
          <div className="flex items-center space-x-3">
            {step > 1 && step < 4 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            )}
            
            {step === 2 && (
              <Button onClick={handlePreview}>
                Preview Data
              </Button>
            )}
            
            {step === 3 && (
              <Button onClick={handleImport}>
                Import Vendors
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorImportModal;
